import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAppSelector } from '../../store/hooks';

interface DatePickerProps {
  value?: string;
  onChange: (value: string) => void;
  minDate?: string;
  maxDate?: string;
  placeholder?: string;
  label?: string;
  error?: string;
  helpText?: string;
  disabled?: boolean;
  className?: string;
  required?: boolean;
}

const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  minDate,
  maxDate,
  placeholder = 'Select date',
  label,
  error,
  helpText,
  disabled = false,
  className = '',
  required = false
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [dropdownCoords, setDropdownCoords] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);

  // Parse the current value using useMemo to prevent unnecessary re-renders
  const selectedDate = useMemo(() => {
    return value ? new Date(value) : null;
  }, [value]);
  
  const today = useMemo(() => new Date(), []);
  // Parse date strings to local date (avoid UTC timezone issues)
  const minDateObj = useMemo(() => {
    if (!minDate) return null;
    // If minDate is in YYYY-MM-DD format, parse it as local date
    const parts = minDate.split('-');
    if (parts.length === 3) {
      return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    }
    return new Date(minDate);
  }, [minDate]);
  const maxDateObj = useMemo(() => {
    if (!maxDate) return null;
    // If maxDate is in YYYY-MM-DD format, parse it as local date
    const parts = maxDate.split('-');
    if (parts.length === 3) {
      return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    }
    return new Date(maxDate);
  }, [maxDate]);

  // Calculate dropdown position based on viewport space
  const calculateDropdownPosition = () => {
    if (!inputRef.current) {
      setDropdownCoords({ top: 0, left: 0, width: 0 });
      return 'below';
    }
    
    const rect = inputRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const dropdownHeight = 350; // Approximate height of the calendar dropdown
    const dropdownWidth = 320; // Approximate width of the calendar dropdown
    
    // Check if there's enough space below
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    
    // Determine if dropdown should appear above or below
    const position = spaceBelow < dropdownHeight && spaceAbove > spaceBelow ? 'above' : 'below';
    
    // Calculate top position
    let top = 0;
    if (position === 'below') {
      top = rect.bottom + 4; // 4px gap (mt-1 equivalent)
    } else {
      top = rect.top - dropdownHeight - 4; // 4px gap (mb-1 equivalent)
    }
    
    // Calculate left position (align with input, but ensure it stays within viewport)
    let left = rect.left;
    if (left + dropdownWidth > viewportWidth) {
      left = viewportWidth - dropdownWidth - 8; // 8px margin from edge
    }
    if (left < 8) {
      left = 8; // 8px margin from edge
    }
    
    setDropdownCoords({
      top: Math.max(8, Math.min(top, viewportHeight - dropdownHeight - 8)), // Ensure it stays within viewport
      left,
      width: rect.width
    });
    
    return position;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;
    
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        containerRef.current && 
        !containerRef.current.contains(target) &&
        !(target instanceof Element && target.closest('[data-datepicker-dropdown]'))
      ) {
        setIsOpen(false);
        setIsFocused(false);
      }
    };

    // Use a slight delay to avoid closing immediately when opening
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Update dropdown position when opening
  useEffect(() => {
    if (isOpen) {
      calculateDropdownPosition();
    }
  }, [isOpen]);

  // Recalculate position on window resize and scroll
  useEffect(() => {
    if (!isOpen) return;

    const handleResize = () => {
      calculateDropdownPosition();
    };

    const handleScroll = () => {
      calculateDropdownPosition();
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true); // Use capture phase to catch all scroll events

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  // Set current month to selected date when value changes
  useEffect(() => {
    if (value && selectedDate) {
      setCurrentMonth(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));
    }
  }, [value, selectedDate]); // selectedDate is memoized from value, so safe to include

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days = [];
    const currentDate = new Date(startDate);
    
    // Generate 42 days (6 weeks)
    for (let i = 0; i < 42; i++) {
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return days;
  };

  const handleDateSelect = (date: Date) => {
    // Format date using local timezone to avoid timezone conversion issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    onChange(dateString);
    setIsOpen(false);
    setIsFocused(false);
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const isDateDisabled = (date: Date) => {
    // Normalize dates to midnight local time for proper comparison
    const normalizeDate = (d: Date) => {
      const normalized = new Date(d);
      normalized.setHours(0, 0, 0, 0);
      return normalized;
    };
    
    const normalizedDate = normalizeDate(date);
    
    if (minDateObj) {
      const normalizedMinDate = normalizeDate(minDateObj);
      if (normalizedDate < normalizedMinDate) return true;
    }
    if (maxDateObj) {
      const normalizedMaxDate = normalizeDate(maxDateObj);
      if (normalizedDate > normalizedMaxDate) return true;
    }
    return false;
  };

  const isDateSelected = (date: Date) => {
    return selectedDate && 
           date.getDate() === selectedDate.getDate() &&
           date.getMonth() === selectedDate.getMonth() &&
           date.getFullYear() === selectedDate.getFullYear();
  };

  const isToday = (date: Date) => {
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const isCurrentMonth = (date: Date) => {
    return date.getMonth() === currentMonth.getMonth();
  };

  const formatDisplayValue = () => {
    if (!value) return placeholder;
    return selectedDate ? selectedDate.toLocaleDateString() : value;
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {/* Label */}
      {label && (
        <label className={`block text-sm font-medium mb-2 ${
          theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
        }`}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Input Button */}
      <div
        ref={inputRef}
        className={`relative cursor-pointer ${
          disabled ? 'opacity-50 cursor-not-allowed' : ''
        }`}
        onClick={() => {
          if (!disabled) {
            setIsOpen(!isOpen);
            setIsFocused(!isOpen);
          }
        }}
        onFocus={() => !disabled && setIsFocused(true)}
        onBlur={() => {
          // Delay blur to allow click events to process
          setTimeout(() => {
            if (!isOpen) {
              setIsFocused(false);
            }
          }, 200);
        }}
        tabIndex={disabled ? -1 : 0}
        role="button"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        <div className={`w-full px-4 py-3 border rounded-lg shadow-sm transition-all ${
          isFocused || isOpen
            ? 'ring-2 ring-mirsal-green ring-opacity-20 border-mirsal-green'
            : ''
        } ${
          theme === 'dark' 
            ? 'border-gray-600 bg-gray-800 text-white' 
            : 'border-gray-300 bg-white text-gray-900'
        } ${error ? 'border-red-500' : ''}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Calendar className={`h-4 w-4 mr-2 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`} />
              <span className={`${
                !value ? theme === 'dark' ? 'text-gray-400' : 'text-gray-500' : ''
              }`}>
                {formatDisplayValue()}
              </span>
            </div>
            <ChevronRight className={`h-4 w-4 transition-transform ${
              isOpen ? 'rotate-90' : ''
            } ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
          </div>
        </div>
      </div>

      {/* Calendar Dropdown - Rendered via Portal */}
      {isOpen && typeof document !== 'undefined' && createPortal(
        <div 
          data-datepicker-dropdown
          className={`fixed z-[9999] rounded-md shadow-lg ring-1 ring-black ring-opacity-5 ${
            theme === 'dark' 
              ? 'bg-gray-800 border border-gray-700' 
              : 'bg-white border border-gray-200'
          }`}
          style={{
            top: `${dropdownCoords.top}px`,
            left: `${dropdownCoords.left}px`,
            width: `${dropdownCoords.width || 320}px`,
            minWidth: '280px'
          }}
        >
          {/* Calendar Header */}
          <div className={`flex items-center justify-between p-3 border-b ${
            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <button
              onClick={handlePrevMonth}
              className={`p-1 rounded-md hover:bg-opacity-20 ${
                theme === 'dark' 
                  ? 'text-gray-300 hover:bg-gray-700' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            
            <h3 className={`text-sm font-semibold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </h3>
            
            <button
              onClick={handleNextMonth}
              className={`p-1 rounded-md hover:bg-opacity-20 ${
                theme === 'dark' 
                  ? 'text-gray-300 hover:bg-gray-700' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {/* Calendar Grid */}
          <div className="p-3">
            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {dayNames.map((day) => (
                <div
                  key={day}
                  className={`text-xs font-medium text-center py-2 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-1">
              {generateCalendarDays().map((date, index) => {
                const isDisabled = isDateDisabled(date);
                const isSelected = isDateSelected(date);
                const isTodayDate = isToday(date);
                const isCurrentMonthDate = isCurrentMonth(date);

                return (
                  <button
                    key={index}
                    onClick={() => !isDisabled && handleDateSelect(date)}
                    disabled={isDisabled}
                    className={`w-8 h-8 text-xs rounded-md transition-colors ${
                      isSelected
                        ? 'bg-mirsal-green text-white'
                        : isTodayDate
                        ? theme === 'dark'
                          ? 'bg-gray-700 text-white border border-mirsal-green'
                          : 'bg-blue-100 text-blue-600 border border-blue-300'
                        : isCurrentMonthDate
                        ? theme === 'dark'
                          ? 'text-white hover:bg-gray-700'
                          : 'text-gray-900 hover:bg-gray-100'
                        : theme === 'dark'
                        ? 'text-gray-500 hover:bg-gray-700'
                        : 'text-gray-400 hover:bg-gray-100'
                    } ${
                      isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                    }`}
                  >
                    {date.getDate()}
                  </button>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Error Message */}
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}

      {/* Help Text */}
      {helpText && !error && (
        <p className={`mt-1 text-xs ${
          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
        }`}>
          {helpText}
        </p>
      )}
    </div>
  );
};

export default DatePicker;
